import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { TitleProps, Title } from "./Title";
import { SimpleRichTextProps, SimpleRichText } from "./SimpleRichText";
import { ChevronIcon } from "./ChevronIcon";
import { ImageWithAspectRatioProps, LinkProps } from "./types";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import { addUtmIfOutbound } from "./utils/url";

export const cardArticle = tv({
  base: cn(
    "uipm:flex uipm:h-full uipm:font-[Inter] uipm:font-normal uipm:rounded-none uipm:md:rounded-[var(--radius-lg)] uipm:overflow-hidden"
  ),
  variants: {
    layout: {
      vertical: "uipm:flex-col",
      horizontal: "uipm:flex-row",
    },
    backgroundColor: {
      primary: "uipm:bg-[var(--surface-surface-accent)]",
      secondary: "uipm:bg-[var(--surface-surface-accent-secondary)]",
      "secondary-light": "uipm:bg-[var(--surface-surface-accent-secondary)]",
      light: "uipm:bg-[var(--surface-surface-secondary)]",
      "very-light": "uipm:bg-[var(--surface-surface-secondary)]",
      white: "uipm:bg-[var(--surface-surface-primary)]",
      none: "uipm:bg-transparent",
    },
    hasLink: {
      true: "uipm:cursor-pointer",
      false: "uipm:cursor-default",
    },
  },
  defaultVariants: {
    layout: "vertical",
    backgroundColor: "primary",
    hasLink: false,
  },
});

const contentPadding = tv({
  variants: {
    verticalPadding: {
      default: "uipm:py-[var(--padding-padding-m)]",
      small: "uipm:py-[var(--container-card-padding)]",
      medium: "uipm:py-[var(--container-card-medium-padding)]",
      large: "uipm:py-[var(--container-card-large-padding)]",
    },
    horizontalPadding: {
      default: "uipm:px-[var(--padding-padding-m)]",
      small: "uipm:px-[var(--container-card-padding)]",
      medium: "uipm:px-[var(--container-card-medium-padding)]",
      large: "uipm:px-[var(--container-card-large-padding)]",
    },
  },
  defaultVariants: {
    verticalPadding: "default",
    horizontalPadding: "default",
  },
});

const titleWrapper = tv({
  base: "uipm:font-[Inter] uipm:font-medium uipm:text-[18px] uipm:leading-[26px]",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted)]",
      secondary: "uipm:text-[var(--surface-surface-accent)]",
      "secondary-light": "uipm:text-[var(--surface-surface-accent)]",
      light: "uipm:text-[var(--surface-surface-accent)]",
      "very-light": "uipm:text-[var(--surface-surface-accent)]",
      white: "uipm:text-[var(--surface-surface-accent)]",
      none: "uipm:text-[var(--surface-surface-accent)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

const textWrapper = tv({
  base: "uipm:font-normal",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted-faded)]",
      secondary: "uipm:text-[var(--typography-colors-text-accent)]",
      "secondary-light": "uipm:text-[var(--typography-colors-text-accent)]",
      light: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      "very-light": "uipm:text-[var(--typography-colors-text-faded-dark)]",
      white: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      none: "uipm:text-[var(--typography-colors-text-faded-dark)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

const linkWrapper = tv({
  base: "uipm:font-normal",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted)]",
      secondary: "uipm:text-[var(--typography-colors-link)]",
      "secondary-light": "uipm:text-[var(--typography-colors-link)]",
      light: "uipm:text-[var(--typography-colors-link)]",
      "very-light": "uipm:text-[var(--typography-colors-link)]",
      white: "uipm:text-[var(--typography-colors-link)]",
      none: "uipm:text-[var(--typography-colors-link)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

const dateWrapper = tv({
  base: "uipm:font-[Inter] uipm:font-normal uipm:fz-[var(--typography-sizes-body-small)] uipm:leading-[var(--typography-line-heights-body-xs)] uipm:text-right",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted)]",
      secondary: "uipm:text-[var(--typography-colors-link)]",
      "secondary-light": "uipm:text-[var(--typography-colors-link)]",
      light: "uipm:text-[var(--typography-colors-link)]",
      "very-light": "uipm:text-[var(--typography-colors-link)]",
      white: "uipm:text-[var(--typography-colors-link)]",
      none: "uipm:text-[var(--typography-colors-link)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

export interface CardArticleProps extends Omit<ComponentProps<"div">, "title"> {
  layout?: "vertical" | "horizontal";
  backgroundColor?:
    | "primary"
    | "secondary"
    | "secondary-light"
    | "light"
    | "very-light"
    | "white"
    | "none";
  verticalPadding?: "default" | "small" | "medium" | "large";
  horizontalPadding?: "default" | "small" | "medium" | "large";
  image?: ImageWithAspectRatioProps;
  title?: TitleProps;
  text?: SimpleRichTextProps;
  link?: LinkProps;
  date?: string;
  onCardClick?: (title?: string, date?: string, linkText?: string, linkHref?: string) => void;
}

// Helper function to format date
const formatDate = (dateString?: string): string | undefined => {
  if (!dateString) return undefined;

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateString; // Return original if parsing fails
  }
};

export const CardArticle = React.forwardRef<HTMLDivElement, CardArticleProps>(
  (
    {
      layout = "vertical",
      backgroundColor = "primary",
      verticalPadding = "default",
      horizontalPadding = "default",
      image,
      title,
      text,
      link,
      date,
      onCardClick,
      className,
      ...props
    },
    ref
  ) => {
    const hasLink = Boolean(link?.href);
    const formattedDate = formatDate(date);

    const renderVerticalLayout = () => (
      <>
        {/* Part 1: Image Section */}
        {image && (
          <div className="uipm:w-full uipm:min-h-[180px] uipm:flex-shrink-0 uipm:flex uipm:justify-center">
            <ImageWithAspectRatio
              {...image}
              imageSpace={"fill"}
              sizes="(max-width: 768px) 100vw, 400px"
              className="uipm:rounded-none uipm:md:rounded-t-[var(--radius-lg)]"
              isFullWidth={true}
            />
          </div>
        )}

        {/* Part 2: Content Section */}
        <div
          className={cn(
            "uipm:flex uipm:flex-col uipm:flex-1 uipm:p-[var(--container-margin)]"
            // contentPadding({ verticalPadding, horizontalPadding })
          )}
        >
          {/* Sub-part 1: Title */}
          {title && (
            <Title
              {...title}
              className={cn(titleWrapper({ backgroundColor }), title.className)}
            />
          )}

          {/* Sub-part 2: Text */}
          {text && (
            <div className={cn("uipm:mt-6")}>
              <SimpleRichText
                {...text}
                className={cn(textWrapper({ backgroundColor }))}
              />
            </div>
          )}

          {/* Sub-part 3: Link + Date */}
          {(link || formattedDate) && (
            <div className="uipm:flex uipm:items-center uipm:justify-between uipm:mt-auto uipm:pt-6">
              {link && (
                <div className="uipm:flex uipm:items-center uipm:gap-2">
                  <span className={linkWrapper({ backgroundColor })}>
                    {link.text}
                  </span>
                  <ChevronIcon
                    className={linkWrapper({ backgroundColor })}
                    size={16}
                  />
                </div>
              )}

              {formattedDate && (
                <div className={dateWrapper({ backgroundColor })}>
                  {formattedDate}
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );

    const renderHorizontalLayout = () => (
      <>
        {/* Part 1: Image Section */}
        {image && (
          <div className="uipm:w-1/2 uipm:flex-shrink-0 uipm:flex uipm:justify-center">
            <ImageWithAspectRatio
              {...image}
              imageSpace={"fill"}
              sizes="(max-width: 768px) 50vw, 200px"
              className="uipm:rounded-none uipm:md:rounded-l-[var(--radius-lg)]"
            />
          </div>
        )}

        {/* Part 2: Content Section */}
        <div
          className={cn(
            "uipm:flex uipm:flex-col uipm:flex-1 uipm:justify-between uipm:p-[var(--container-margin)]"
            // contentPadding({ verticalPadding, horizontalPadding })
          )}
        >
          <div>
            {/* Sub-part 1: Title + Text */}
            <div className="uipm:flex uipm:flex-col">
              {title && (
                <Title
                  {...title}
                  className={cn(
                    titleWrapper({ backgroundColor }),
                    title.className
                  )}
                />
              )}

              {text && (
                <div
                  className={cn("uipm:mt-6", textWrapper({ backgroundColor }))}
                >
                  <SimpleRichText {...text} />
                </div>
              )}
            </div>
          </div>
          {/* Sub-part 2: Link + Date */}
          {(link || formattedDate) && (
            <div className="uipm:flex uipm:items-center uipm:justify-between uipm:mt-auto uipm:pt-6">
              {link && (
                <div className="uipm:flex uipm:items-center uipm:gap-2">
                  <span className={linkWrapper({ backgroundColor })}>
                    {link.text}
                  </span>
                  <ChevronIcon
                    className={linkWrapper({ backgroundColor })}
                    size={16}
                  />
                </div>
              )}

              {formattedDate && (
                <div className={dateWrapper({ backgroundColor })}>
                  {formattedDate}
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );

    const cardContent =
      layout === "vertical" ? renderVerticalLayout() : renderHorizontalLayout();

    const handleClick = () => {
      onCardClick?.(title?.content, formattedDate, link?.text, link?.href);
    };

    if (hasLink) {
      return (
        <a
          href={link?.href && addUtmIfOutbound(link.href)}
          target={link?.target}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={cn(
            cardArticle({ backgroundColor, hasLink, layout }),
            className
          )}
          onClick={handleClick}
          {...(props as ComponentProps<"a">)}
        >
          {cardContent}
        </a>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          cardArticle({ backgroundColor, hasLink, layout }),
          className
        )}
        onClick={handleClick}
        {...props}
      >
        {cardContent}
      </div>
    );
  }
);

export type CardArticleVariants = VariantProps<typeof cardArticle>;

CardArticle.displayName = "CardArticle";
